import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConsultationsTab = ({ onNewConsultation }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const consultations = [
    {
      id: "CONS-2025-001",
      date: "2025-09-20",
      time: "10:30 AM",
      type: "Video Consultation",
      provider: "Dr. Sarah Johnson",
      specialty: "Internal Medicine",
      status: "Completed",
      duration: "45 minutes",
      chiefComplaint: "Follow-up for hypertension management",
      diagnosis: "Essential hypertension, well-controlled",
      notes: `Patient reports good adherence to medication regimen. Blood pressure readings at home have been consistently within target range (120-130/70-80 mmHg).\n\nNo adverse effects from current medications. Patient feels well overall with good energy levels.\n\nRecommended to continue current medication regimen and follow up in 3 months.`,
      prescriptions: ["Lisinopril 10mg - Continue current dose"],
      followUp: "3 months",
      attachments: ["BP_Log_Sept2025.pdf", "Lab_Results_Sept2025.pdf"]
    },
    {
      id: "CONS-2025-002",
      date: "2025-09-15",
      time: "2:15 PM",
      type: "In-Person",
      provider: "Dr. Michael Chen",
      specialty: "Endocrinology",
      status: "Completed",
      duration: "30 minutes",
      chiefComplaint: "Diabetes management and HbA1c review",
      diagnosis: "Type 2 diabetes mellitus, well-controlled",
      notes: `HbA1c results show excellent control at 6.8%. Patient has been following dietary recommendations and exercise routine consistently.\n\nNo signs of diabetic complications. Foot examination normal, no neuropathy detected.\n\nContinue current management plan with lifestyle modifications.`,
      prescriptions: ["Metformin 500mg - Continue twice daily"],
      followUp: "6 months",
      attachments: ["HbA1c_Results_Sept2025.pdf"]
    },
    {
      id: "CONS-2025-003",
      date: "2025-09-10",
      time: "11:00 AM",
      type: "Telehealth",
      provider: "Dr. Emily Rodriguez",
      specialty: "Pulmonology",
      status: "Completed",
      duration: "25 minutes",
      chiefComplaint: "Asthma inhaler refill and symptom check",
      diagnosis: "Asthma, stable",
      notes: `Patient reports minimal asthma symptoms over the past 3 months. Using rescue inhaler less than once per week.\n\nSpirometry results from last visit show good lung function. No changes needed to current treatment plan.\n\nProvided inhaler technique review via video demonstration.`,
      prescriptions: ["Albuterol Inhaler - Refill provided"],
      followUp: "As needed",
      attachments: []
    }
  ];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-success text-success-foreground';
      case 'scheduled': return 'bg-primary text-primary-foreground';
      case 'cancelled': return 'bg-error text-error-foreground';
      case 'no-show': return 'bg-warning text-warning-foreground';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'video consultation': return 'Video';
      case 'telehealth': return 'Phone';
      case 'in-person': return 'User';
      default: return 'Calendar';
    }
  };

  const filteredConsultations = consultations?.filter(consultation => {
    const matchesSearch = consultation?.provider?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         consultation?.specialty?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         consultation?.chiefComplaint?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesFilter = filterType === 'all' || consultation?.type?.toLowerCase()?.includes(filterType?.toLowerCase());
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Search and Filter Controls */}
      <div className="bg-card border border-border rounded-lg p-4 shadow-clinical">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search consultations by provider, specialty, or complaint..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="px-4 py-2 bg-input border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Types</option>
              <option value="video">Video Consultation</option>
              <option value="telehealth">Telehealth</option>
              <option value="in-person">In-Person</option>
            </select>
            
            <Button variant="default" iconName="Plus" iconPosition="left" onClick={onNewConsultation}>
              New Consultation
            </Button>
          </div>
        </div>
      </div>
      {/* Consultations List */}
      <div className="space-y-4">
        {filteredConsultations?.map((consultation) => (
          <div key={consultation?.id} className="bg-card border border-border rounded-lg shadow-clinical">
            {/* Consultation Header */}
            <div className="p-6 border-b border-border">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={getTypeIcon(consultation?.type)} size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-semibold text-foreground">{consultation?.provider}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(consultation?.status)}`}>
                        {consultation?.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        {new Date(consultation.date)?.toLocaleDateString()} at {consultation?.time}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Clock" size={14} />
                        {consultation?.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Stethoscope" size={14} />
                        {consultation?.specialty}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" iconName="FileText">
                    View Notes
                  </Button>
                  <Button variant="outline" size="sm" iconName="Download">
                    Export
                  </Button>
                </div>
              </div>
            </div>

            {/* Consultation Details */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Chief Complaint</h4>
                    <p className="text-sm text-muted-foreground">{consultation?.chiefComplaint}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Diagnosis</h4>
                    <p className="text-sm text-muted-foreground">{consultation?.diagnosis}</p>
                  </div>
                  
                  {consultation?.prescriptions?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-foreground mb-2">Prescriptions</h4>
                      <ul className="space-y-1">
                        {consultation?.prescriptions?.map((prescription, index) => (
                          <li key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                            <Icon name="Pill" size={14} />
                            {prescription}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Clinical Notes</h4>
                    <div className="bg-muted/30 rounded-lg p-3">
                      <p className="text-sm text-muted-foreground whitespace-pre-line">{consultation?.notes}</p>
                    </div>
                  </div>
                  
                  {consultation?.attachments?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-foreground mb-2">Attachments</h4>
                      <div className="space-y-2">
                        {consultation?.attachments?.map((attachment, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                            <div className="flex items-center gap-2">
                              <Icon name="FileText" size={16} className="text-muted-foreground" />
                              <span className="text-sm text-foreground">{attachment}</span>
                            </div>
                            <Button variant="ghost" size="sm" iconName="Download">
                              Download
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Follow-up</h4>
                    <p className="text-sm text-muted-foreground">{consultation?.followUp}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredConsultations?.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-12 text-center shadow-clinical">
          <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No consultations found</h3>
          <p className="text-muted-foreground mb-6">No consultations match your current search criteria.</p>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={onNewConsultation}>
            Schedule New Consultation
          </Button>
        </div>
      )}
    </div>
  );
};

export default ConsultationsTab;